import { collection } from '../../infra/collection'
import {
  DeclineOpportunityOfferRequest,
  GetOpportunityOfferRequest,
  ListOpportunityOffersRequest,
  OpportunityOffer as OpportunityOfferProto,
} from '@taxfyle/api-internal/internal/opportunity_pb'
import { OpportunityOffer } from './OpportunityOffer'
import { OpportunitiesAPI } from '../API'
import { PrimeUser } from '../../iam/UserStore'
import { PrimeWorkspace } from '../../iam/WorkspaceStore'
import { GetCurrentMember } from '../../iam/SessionStore'
import { Id } from '../../types/definitions'
import { IJobsPort } from './ports'
import { defer, merge, mergeMap, switchMap, Observable } from 'rxjs'
import { filterNonNil, whileActive } from '../../utils/rx-util'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { filter, find, map, tap, share } from 'rxjs/operators'
import TypeCase = RealtimeEvent.TypeCase

/**
 * Store for Opportunity Offers.
 */
export class OpportunityOfferStore {
  /**
   * The Opportunity Offers collection.
   */
  opportunityOffers = collection<
    OpportunityOfferProto.AsObject,
    OpportunityOffer
  >({
    create: () =>
      new OpportunityOffer(
        this.primeUser,
        this.primeWorkspace,
        this.getCurrentMember
      ),
  })

  /**
   * Emits pending offers. Used on first load.
   */
  private readonly _offersFromFetch$ = defer(async () => {
    const request = new ListOpportunityOffersRequest().setPageSize(100)
    return this.opsAPI.listOpportunityOffers(request)
  }).pipe(
    mergeMap((resp) => resp.getOffersList()),
    map((x) => x.toObject())
  )

  /**
   * Observable of incoming offers.
   */
  readonly incomingOffers$ = merge(
    this._offersFromFetch$,
    this.opsAPI.offerEvents$.pipe(
      filter((x) => x.getTypeCase() === TypeCase.OPPORTUNITY_OFFERED),
      map((x) => x.getOpportunityOffered()!.toObject()),
      filterNonNil()
    )
  ).pipe(
    whileActive(this.canViewOpportunities$),
    filter((x) => !this.opportunityOffers.get(x.id)),
    map((x) => this.opportunityOffers.set(x)),
    share()
  )

  /**
   * Observable of removed offers.
   */
  readonly offersRemoved$ = this.canViewOpportunities$.pipe(
    find(Boolean),
    switchMap(() => this.opportunityOffers.removals$)
  )

  /**
   * Constructor.
   */
  constructor(
    private opsAPI: OpportunitiesAPI,
    private jobsPort: IJobsPort,
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentMember: GetCurrentMember,
    private readonly canViewOpportunities$: Observable<boolean>
  ) {
    this.opsAPI.offerEvents$
      .pipe(
        whileActive(this.canViewOpportunities$),
        filter((x) => x.getTypeCase() === TypeCase.OPPORTUNITY_OFFER_REMOVED),
        map((x) => x.getOpportunityOfferRemoved()!.toObject()),
        filterNonNil(),
        tap((x) => this.opportunityOffers.remove(x.id))
      )
      .subscribe()
  }

  /**
   * Fetches a singular opportunity offer.
   */
  async getOpportunityOffer(offerId: Id): Promise<OpportunityOffer> {
    const query = new GetOpportunityOfferRequest().setId(Id.assert(offerId))

    const offer = new OpportunityOffer(
      this.primeUser,
      this.primeWorkspace,
      this.getCurrentMember
    )

    return this.opsAPI
      .getOpportunityOffer(query)
      .then((res) => offer.set(offer.parse(res.toObject())))
  }

  /**
   * Accepts the given opportunity.
   */
  async acceptOffer(opportunityId: Id) {
    const id = Id.assert(opportunityId)

    return this.jobsPort
      .acceptOffer(id)
      .then(() => this.opportunityOffers.remove(id))
  }

  /**
   * Declines the given opportunity.
   */
  async declineOffer(opportunityId: Id) {
    const id = Id.assert(opportunityId)

    return this.opsAPI
      .declineOffer(new DeclineOpportunityOfferRequest().setId(id))
      .then(() => this.opportunityOffers.remove(id))
  }
}
